import { encodeQueryStringValue } from "utils/search/buildSearchLink";

import { isEmpty } from "lodash";
import { normalize } from "normalizr";
import { v4 as uuidv4 } from "uuid";
import RequestError from "utils/error/request";
import { forwardLog } from "utils/logForwarder";

export default function callAPI({
	url,
	options = {
		query: {},
		body: {},
		headers: {},
		method: "GET"
	},
	schema,
	staticMapUrl = false,
	traceId: externalTraceId
}) {
	const queryString = !isEmpty(options.query)
		? `?${encodeQueryStringValue(options.query)}`
		: "";

	const traceId = externalTraceId || uuidv4();
	const method = options.method || "GET";
	const fullUrl = `${url}${queryString}`;

	const requestOptions = {
		headers: {
			...(options.headers || {}),
			"X-Trace-Id": traceId
		},
		method,
		body: options.body ? JSON.stringify(options.body) : undefined
	};

	const initMsg = `[X-Trace-Id: ${traceId}] → ${method} ${fullUrl} | Request initiated`;
	// eslint-disable-next-line no-console
	console.log(initMsg);
	forwardLog({ level: "info", message: initMsg, "x-trace-id": traceId });
	const startTime = Date.now();

	return fetch(fullUrl, requestOptions)
		.then(async (res) => {
			const duration = Date.now() - startTime;
			const successMsg = `[X-Trace-Id: ${traceId}] ← ${method} ${fullUrl} | Status: ${res.status} | ${duration}ms`;
			// eslint-disable-next-line no-console
			console.log(successMsg);
			forwardLog({ level: "info", message: successMsg, "x-trace-id": traceId });
			try {
				if (res.status === 401 || res.status === 403) {
					return {
						status: res.status,
						statusText: res.statusText
					};
				}
				return res.json();
			} catch (error) {
				const jsonErrMsg = `[X-Trace-Id: ${traceId}] ✕ ${method} ${fullUrl} | Invalid JSON response | ${duration}ms`;
				// eslint-disable-next-line no-console
				console.error(jsonErrMsg);
				forwardLog({ level: "error", message: jsonErrMsg, "x-trace-id": traceId });
				throw error;
			}
		})
		.then((data) => {
			if (schema) {
				// If the response is unauthorized or forbidden, return the status and status text
				if (data.status === 401 || data.status === 403) {
					return {
						status: data.status,
						statusText: data.statusText
					};
				}
				return {
					...normalize(data, schema),
					meta: data.meta || {},
					...(staticMapUrl && { staticMapUrl: data.staticMapUrl })
				};
			}
			return data;
		})
		.catch((error) => {
			const duration = Date.now() - startTime;
			const errorMsg = `[X-Trace-Id: ${traceId}] ✕ ${method} ${fullUrl} | Error: ${error?.message || "Unknown"} | ${duration}ms`;
			// eslint-disable-next-line no-console
			console.error(errorMsg);
			forwardLog({ level: "error", message: errorMsg, "x-trace-id": traceId });
			throw new RequestError(error);
		});
}
